import { useEffect, useState } from 'react';
import MentorshipService, {
    MentorshipOfflineLoggingPreferences,
    MentorshipWhoCanLogOffline,
} from './MentorshipService';

interface MentorshipSessionsPlatformProps {
    cohortId: number | null;
    disabled?: boolean;
}

const JIRA_DEFAULTS: MentorshipOfflineLoggingPreferences = {
    whoCanLogOffline: 'ADMIN_ONLY',
    modeZoomTeamsMeet: true,
    modeTelephonic: true,
    modeFacetimeWhatsapp: true,
    modeLinkedin: true,
    modeInPerson: true,
    modeOthers: true,
};

const WHO_CAN_LOG_OPTIONS: { value: MentorshipWhoCanLogOffline; label: string }[] = [
    { value: 'ANY_PARTICIPANT', label: 'Any Participant' },
    { value: 'MENTOR_AND_ADMIN', label: 'Only Mentor and Admin' },
    { value: 'ADMIN_ONLY', label: 'Only Admin' },
];

const MODE_SWITCHES: {
    key: keyof Omit<MentorshipOfflineLoggingPreferences, 'whoCanLogOffline'>;
    label: string;
}[] = [
    { key: 'modeZoomTeamsMeet', label: 'Zoom/Teams/Google Meet Call' },
    { key: 'modeTelephonic', label: 'Telephonic Call' },
    { key: 'modeFacetimeWhatsapp', label: 'Facetime/WhatsApp conversation' },
    { key: 'modeLinkedin', label: 'LinkedIn Conversation' },
    { key: 'modeInPerson', label: 'In-person Meeting' },
    { key: 'modeOthers', label: 'Others' },
];

const cloneConfig = (value: MentorshipOfflineLoggingPreferences): MentorshipOfflineLoggingPreferences => ({
    whoCanLogOffline: value.whoCanLogOffline,
    modeZoomTeamsMeet: value.modeZoomTeamsMeet === true,
    modeTelephonic: value.modeTelephonic === true,
    modeFacetimeWhatsapp: value.modeFacetimeWhatsapp === true,
    modeLinkedin: value.modeLinkedin === true,
    modeInPerson: value.modeInPerson === true,
    modeOthers: value.modeOthers === true,
});

const configEqual = (a: MentorshipOfflineLoggingPreferences, b: MentorshipOfflineLoggingPreferences) =>
    a.whoCanLogOffline === b.whoCanLogOffline &&
    a.modeZoomTeamsMeet === b.modeZoomTeamsMeet &&
    a.modeTelephonic === b.modeTelephonic &&
    a.modeFacetimeWhatsapp === b.modeFacetimeWhatsapp &&
    a.modeLinkedin === b.modeLinkedin &&
    a.modeInPerson === b.modeInPerson &&
    a.modeOthers === b.modeOthers;

const MentorshipSessionsPlatform = ({ cohortId, disabled = false }: MentorshipSessionsPlatformProps) => {
    const cohortAvailable = cohortId != null && !disabled;
    const [loaded, setLoaded] = useState<MentorshipOfflineLoggingPreferences | null>(null);
    const [draft, setDraft] = useState<MentorshipOfflineLoggingPreferences>(JIRA_DEFAULTS);
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [successMessage, setSuccessMessage] = useState('');

    useEffect(() => {
        if (!cohortAvailable || cohortId == null) {
            setLoaded(null);
            setDraft(JIRA_DEFAULTS);
            setErrorMessage('');
            setSuccessMessage('');
            setIsLoading(false);
            return;
        }

        let cancelled = false;
        const load = async () => {
            setIsLoading(true);
            setErrorMessage('');
            setSuccessMessage('');
            try {
                const next = await MentorshipService.getOfflineLoggingPreferences(cohortId);
                if (cancelled) return;
                const cloned = cloneConfig(next);
                setLoaded(cloned);
                setDraft(cloneConfig(next));
            } catch (error) {
                if (cancelled) return;
                setLoaded(null);
                setDraft(JIRA_DEFAULTS);
                setErrorMessage(MentorshipService.getErrorMessage(error));
            } finally {
                if (!cancelled) setIsLoading(false);
            }
        };
        load();
        return () => {
            cancelled = true;
        };
    }, [cohortAvailable, cohortId]);

    const dirty = loaded != null && !configEqual(draft, loaded);
    const actionsDisabled = !cohortAvailable || isLoading || isSaving || loaded == null;

    const handleSave = async () => {
        if (cohortId == null || actionsDisabled || !dirty) return;
        setIsSaving(true);
        setErrorMessage('');
        setSuccessMessage('');
        try {
            const saved = await MentorshipService.saveOfflineLoggingPreferences(cohortId, draft);
            const cloned = cloneConfig(saved);
            setLoaded(cloned);
            setDraft(cloneConfig(saved));
            setSuccessMessage('Settings saved.');
        } catch (error) {
            setErrorMessage(MentorshipService.getErrorMessage(error));
        } finally {
            setIsSaving(false);
        }
    };

    const handleDiscard = () => {
        if (loaded == null) return;
        setDraft(cloneConfig(loaded));
        setErrorMessage('');
        setSuccessMessage('');
    };

    const setWhoCanLog = (value: string) => {
        if (value !== 'ANY_PARTICIPANT' && value !== 'MENTOR_AND_ADMIN' && value !== 'ADMIN_ONLY') {
            return;
        }
        setDraft((current) => ({ ...current, whoCanLogOffline: value }));
        setSuccessMessage('');
    };

    const toggleMode = (key: keyof Omit<MentorshipOfflineLoggingPreferences, 'whoCanLogOffline'>) => {
        setDraft((current) => ({ ...current, [key]: !current[key] }));
        setSuccessMessage('');
    };

    return (
        <div>
            <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
                <h3 className="font-20 font-600 mb-0">Sessions & Platform</h3>
                <div className="d-flex flex-wrap gap-2">
                    <button type="button" className="btn btn-success" disabled={actionsDisabled || !dirty} onClick={handleSave}>
                        Save Settings
                    </button>
                    <button
                        type="button"
                        className="btn btn-outline-secondary"
                        disabled={actionsDisabled || !dirty}
                        onClick={handleDiscard}>
                        Discard
                    </button>
                </div>
            </div>

            {errorMessage && <div className="alert alert-danger">{errorMessage}</div>}
            {successMessage && <div className="alert alert-success">{successMessage}</div>}

            {!cohortAvailable && (
                <div className="text-muted mb-3">Select a cohort to manage Sessions & Platform.</div>
            )}
            {cohortAvailable && isLoading && (
                <div className="text-muted mb-3">Loading Sessions & Platform settings...</div>
            )}

            {cohortAvailable && !isLoading && loaded != null && (
                <fieldset disabled={isSaving}>
                    <div className="row g-3 mb-3">
                        <div className="col-lg-12">
                            <div className="card border-0 rounded-3 shadow-sm bg-white h-100">
                                <div className="card-header bg-white border-bottom py-3 px-4">
                                    <div className="card-title mb-0 font-16 font-700">📋 Offline Interaction Logging</div>
                                    <p className="font-14 text-secondary lh-sm mb-0">Choose who may log off-platform interactions and which modes appear in the log dropdown.</p>
                                </div>
                                <div className="card-body p-4">
                                    <p className="font-14 text-secondary lh-sm mb-3">Participants can log sessions and interactions that happened outside the platform — campus visits, WhatsApp chats, in-person meets. These count towards milestones.</p>
                                    <div className="mb-3">
                                    <label className="form-label font-14 font-700 text-uppercase tracking-wider text-muted" htmlFor="mentorship-offline-who-can-log">Who can log offline interactions?</label>
                                    
                                    <select
                                        id="mentorship-offline-who-can-log"
                                        className="form-select font-13"
                                        value={draft.whoCanLogOffline}
                                        onChange={(event) => setWhoCanLog(event.target.value)}>
                                        {WHO_CAN_LOG_OPTIONS.map((option) => (
                                            <option key={option.value} value={option.value}>
                                                {option.label}
                                            </option>
                                        ))}
                                    </select>
                                    <div className="small text-muted mt-2">
                                        Mentor and mentee logging becomes available with their portals. This stored
                                        setting does not mean those portals exist.
                                    </div>
                                    </div>
                                    <div>
                                        <label className="form-label font-14 font-700 text-uppercase tracking-wider text-muted">Supported Interaction Modes</label>
                                        <p className="small text-muted mb-3">Selected modes appear in the admin log-interaction dropdown.</p>
                                        <div className="d-flex flex-wrap gap-2 mt-1">
                                            {MODE_SWITCHES.map((mode) => (
                                                <div
                                                    key={mode.key}
                                                    className="form-check">
                                                        <input
                                                            id={`mentorship-offline-${mode.key}`}
                                                            className="form-check-input"
                                                            type="checkbox"
                                                            checked={draft[mode.key]}
                                                            onChange={() => toggleMode(mode.key)}
                                                        />
                                                        <label className="form-check-label font-14" htmlFor={`mentorship-offline-${mode.key}`}>
                                                            {mode.label}
                                                        </label>                          
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                                </div>
                        </div>
                    </div>
               

                  


                    
                </fieldset>
            )}
        </div>
    );
};

export default MentorshipSessionsPlatform;
